/** Tynn klient mot backend. Alle kall går via REACT_APP_BACKEND_URL + /api. */
export const API_BASE = `${process.env.REACT_APP_BACKEND_URL ?? ''}/api`;

export class ApiError extends Error {
  constructor(message, status, detail) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

async function parse(res) {
  const text = await res.text();
  let body = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = text;
  }
  if (!res.ok) {
    const detail = body && typeof body === 'object' ? body.detail : body;
    const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map((d) => d.msg ?? JSON.stringify(d)).join(' · ') : `Feil ${res.status}`;
    throw new ApiError(msg, res.status, detail);
  }
  return body;
}

/**
 * @param {string} path
 * @param {{ signal?: AbortSignal, retries?: number, backoffMs?: number }} [opts]
 */
export async function apiGet(path, { signal, retries = 3, backoffMs = 500 } = {}) {
  /* Lesekall prøves på nytt ved midlertidige feil (nettverk, 502/503/504 – typisk når tjenesten starter opp).
     Ventetid 0,5 s → 1 s → 2 s. Avbrutte kall og vanlige API-feil (4xx/500) prøves ikke på nytt. */
  for (let attempt = 0; ; attempt++) {
    try {
      const res = await fetch(`${API_BASE}${path}`, { signal, headers: { Accept: 'application/json' } });
      return await parse(res);
    } catch (e) {
      if (signal?.aborted || e?.name === 'AbortError') throw e;
      if (attempt >= retries || !isTransientError(e)) throw e;
      await sleep(backoffMs * 2 ** attempt);
    }
  }
}

const TRANSIENT_STATUS = new Set([502, 503, 504]);
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Midlertidig feil: nettverksfeil (fetch kaster TypeError) eller gateway-feil mens backend starter. */
export function isTransientError(e) {
  if (!e) return false;
  if (e instanceof ApiError) return TRANSIENT_STATUS.has(e.status);
  return e.name === 'TypeError';
}

export async function apiSend(method, path, body, { signal } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    signal,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return parse(res);
}

export const apiPost = (path, body, opts) => apiSend('POST', path, body, opts);
export const apiPut = (path, body, opts) => apiSend('PUT', path, body, opts);
export const apiPatch = (path, body, opts) => apiSend('PATCH', path, body, opts);
export const apiDelete = (path, opts) => apiSend('DELETE', path, undefined, opts);

/** Filopplasting (multipart/form-data). Content-Type settes av nettleseren (med boundary). */
export async function apiUpload(path, formData, { signal } = {}) {
  const res = await fetch(`${API_BASE}${path}`, { method: 'POST', signal, headers: { Accept: 'application/json' }, body: formData });
  return parse(res);
}

/**
 * Leser en SSE-strøm (text/event-stream) fra et POST-kall.
 * onEvent({ event, data }) kalles for hver melding. Returnerer når strømmen er ferdig.
 */
export async function apiStream(path, body, { onEvent, signal } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    signal,
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify(body),
  });
  if (!res.ok || !res.body) {
    await parse(res);
    throw new ApiError('Strømmen kunne ikke åpnes', res.status);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const flush = (chunk) => {
    const lines = chunk.split('\n');
    let event = 'message';
    const dataLines = [];
    for (const line of lines) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
    }
    if (!dataLines.length) return;
    let data = dataLines.join('\n');
    try {
      data = JSON.parse(data);
    } catch {
      /* rå tekst */
    }
    onEvent?.({ event, data });
  };
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const chunk = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      if (chunk.trim()) flush(chunk);
    }
  }
  if (buffer.trim()) flush(buffer);
}

/** Norsk tallformat med mellomrom som tusenskille. */
export function formatNumber(n, decimals = 0) {
  if (n === null || n === undefined || Number.isNaN(n)) return '–';
  return new Intl.NumberFormat('nb-NO', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(n);
}

export function formatKr(n, decimals = 0) {
  if (n === null || n === undefined) return '–';
  return `${formatNumber(n, decimals)} kr`;
}
